import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { UserService } from '../../../../../services/user.service';
import { District } from '../../../../../models/district';
import { School } from '../../../../../models/school';
import { DistrictAdminService } from '../../../../../services/district-admin.service';
import { ChangeDetectorRef } from '@angular/core';

@Component({
  selector: 'app-add-user-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIcon,
  ],
  templateUrl: './add-user-dialog.html',
  styleUrl: './add-user-dialog.css',
})
export class AddUserDialog {
  userService = inject(UserService);
  schoolService = inject(DistrictAdminService);
  private cdr = inject(ChangeDetectorRef);
  dialogRef = inject(MatDialogRef);
  form = new FormGroup({
    first_name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    last_name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email],
    }),
    user_type: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    school: new FormControl<number | null>(null, {
      nonNullable: true,
    }),
    district: new FormControl<number | null>(null, {
      nonNullable: true,
    }),
  });
  schools: School[] = [];
  districts: District[] = [];
  types: string[] = ['Faculty', 'Student'];

  ngOnInit() {
    this.getDistricts();
    this.getSchools();

    this.form.controls.school.valueChanges.subscribe((schoolId) => {
      if (schoolId === null) return;
      const school = this.schools.find((s) => s.id === schoolId);
      if (school) {
        this.form.controls.district.setValue(school.district_id, { emitEvent: false });
      }
    });

    this.form.controls.district.valueChanges.subscribe((districtId) => {
      const schoolId = this.form.controls.school.value;
      if (schoolId === null) return;
      const school = this.schools.find((s) => s.id === schoolId);
      if (school && school.district_id !== districtId) {
        this.form.controls.school.setValue(null, { emitEvent: false });
      }
    });
  }

  getSchools() {
    this.schoolService.getSchools().subscribe({
      next: (school) => {
        this.schools = school;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to load schools:', err);
      },
    });
  }

  getDistricts() {
    this.schoolService.getDistricts().subscribe({
      next: (district) => {
        this.districts = district;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to load schools:', err);
      },
    });
  }

  addUser(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();

    const payload: Record<string, unknown> = {
      first_name: raw.first_name,
      last_name: raw.last_name,
      email: raw.email,
      user_type: raw.user_type,
    };
    if (raw.school !== null) payload['school'] = raw.school;
    if (raw.district !== null) payload['district'] = raw.district;

    this.userService.createUser(payload).subscribe({
      next: (createdReport) => {
        this.dialogRef.close(createdReport);
      },
      error: (err) => {
        console.error('createReport failed:', err);
      },
    });
  }
}
